import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, MessageSquare, Home } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { useUnreadMessages } from "../lib/unreadMessages";
import { BRAND_COLORS, STATUS_COLORS } from "../lib/bookingStatus";

function formatAddress(prop) {
  if (!prop) return null;
  const parts = [prop.address, prop.city].filter(Boolean);
  return parts.join(", ") || null;
}

export default function TeamCustomers() {
  const { t } = useI18n();
  const { activeConciergeId } = useAuth();
  const [customers, setCustomers] = useState([]);
  const { byThread: unreadByThread } = useUnreadMessages("team");

  const load = () => api.get("/team/customers").then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, []);

  // Feb 2026 · Concierge-Perspektive-Filter (Option b): Kunden erscheinen
  // nur, wenn mindestens EINES ihrer Objekte dem aktiven Concierge zugeteilt
  // ist. Backend liefert `assigned_concierge_ids` als String-Array pro Kunde.
  const visibleCustomers = useMemo(() => {
    if (!activeConciergeId) return customers;
    return customers.filter((c) =>
      (c.assigned_concierge_ids || []).includes(activeConciergeId),
    );
  }, [customers, activeConciergeId]);

  return (
    <div className="max-w-[1500px] mx-auto px-4 md:px-10 py-10 md:py-14 w-full" data-testid="team-customers">
      <div className="mb-10">
        <div className="label-caps mb-3">Weissblom Team</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">{t.team.customers_title}</h1>
      </div>

      <div className="bg-white border border-navy/10 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-navy/10">
              <th className="label-caps text-left px-5 py-4">Startbild</th>
              <th className="label-caps text-left px-5 py-4">Kunde</th>
              <th className="label-caps text-left px-5 py-4">Kontakt</th>
              <th className="label-caps text-left px-5 py-4">Immobilie</th>
              <th className="label-caps text-center px-3 py-4">Obj.</th>
              <th className="label-caps text-center px-3 py-4">Buch.</th>
            </tr>
          </thead>
          <tbody>
            {visibleCustomers.map((c) => {
              const addr = formatAddress(c.primary_property);
              return (
                <tr key={c.id} className="border-b border-navy/5 last:border-b-0 hover:bg-ivory/50 transition-colors align-top" data-testid={`customer-row-${c.id}`}>
                  {/* Feb 2026 · Directive #297: „Startbild"-Box zeigt zuerst
                      das Profilfoto des Kunden (avatar_file_id), sonst als
                      Fallback das Objekt-Hero, sonst einen dezenten neutralen
                      Platzhalter (Home-Icon, kein blaues Fragezeichen). */}
                  <td className="px-5 py-4">
                    {c.avatar_file_id ? (
                      <img
                        src={fileUrl(c.avatar_file_id)}
                        alt={c.name || c.email || ""}
                        data-testid={`customer-avatar-${c.id}`}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className="w-16 h-16 object-cover border border-navy/10"
                      />
                    ) : c.hero_image_url ? (
                      <img
                        src={c.hero_image_url}
                        alt={c.welcome_name || c.name || c.email}
                        data-testid={`hero-thumb-${c.id}`}
                        className="w-16 h-16 object-cover border border-navy/10"
                      />
                    ) : (
                      <div
                        className="w-16 h-16 border border-navy/10 bg-ivory/40 flex items-center justify-center"
                        data-testid={`customer-photo-placeholder-${c.id}`}
                        aria-hidden="true"
                      >
                        <Home
                          className="w-6 h-6"
                          strokeWidth={1.4}
                          style={{ color: `${BRAND_COLORS.navy}40` }}
                        />
                      </div>
                    )}
                  </td>
                  {/* Customer name + welcome_name (Feb 2026 · runder Avatar-Kreis entfernt). */}
                  <td className="px-5 py-4 min-w-[220px]">
                    <div className="min-w-0 flex-1">
                        <Link
                          to={`/team/customers/${c.id}`}
                          data-testid={`customer-open-detail-${c.id}`}
                          className="text-navy font-medium hover:text-gold transition-colors inline-flex items-center gap-1.5"
                        >
                          {c.name || "—"}
                          {unreadByThread[c.id] > 0 && (
                            <Link
                              to="/team/chat"
                              data-testid={`customer-unread-badge-${c.id}`}
                              onClick={(e) => e.stopPropagation()}
                              title={`${unreadByThread[c.id]} ungelesene Nachricht${unreadByThread[c.id] === 1 ? "" : "en"} — direkt zum Chat`}
                              className="relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none shadow-sm hover:brightness-110"
                              style={{ background: STATUS_COLORS.red, color: BRAND_COLORS.white }}
                            >
                              <MessageSquare className="w-3 h-3" strokeWidth={2.2} />
                              {unreadByThread[c.id] > 99 ? "99+" : unreadByThread[c.id]}
                            </Link>
                          )}
                          <ChevronRight className="w-3.5 h-3.5 opacity-60" strokeWidth={1.6} />
                        </Link>
                        {c.welcome_name && (
                          <div className="text-[12px] italic mt-0.5" style={{ color: "#8A7635" }} data-testid={`welcome-name-${c.id}`}>
                            „{c.welcome_name}"
                          </div>
                        )}
                        <div className="label-caps mt-1">{c.language}</div>
                      </div>
                  </td>
                  {/* Contact */}
                  <td className="px-5 py-4 text-[12.5px] text-navy-dark/80 leading-relaxed min-w-[220px]">
                    <a href={`mailto:${c.email}`} className="text-navy-dark hover:text-gold transition-colors">{c.email}</a>
                    {c.phone && (
                      <div className="mt-1" data-testid={`phone-${c.id}`}>
                        <a href={`tel:${c.phone.replace(/\s+/g, "")}`} className="text-navy-dark/70 hover:text-gold transition-colors">{c.phone}</a>
                      </div>
                    )}
                  </td>
                  {/* Address */}
                  <td className="px-5 py-4 text-[12.5px] text-navy-dark/80 leading-relaxed min-w-[200px]" data-testid={`address-${c.id}`}>
                    {addr ? (
                      <>
                        <div>{addr}</div>
                        {c.primary_property?.name && (
                          c.primary_property?.id ? (
                            <Link
                              to={`/team/properties/${c.primary_property.id}`}
                              className="text-[11px] italic mt-0.5 inline-block hover:text-gold transition-colors text-navy-dark/60"
                              data-testid={`primary-property-link-${c.id}`}
                              title="Zur Objekt-Detailansicht"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {c.primary_property.name} <ChevronRight className="inline-block w-3 h-3 opacity-60" strokeWidth={1.8} />
                            </Link>
                          ) : (
                            <div className="text-[11px] text-navy-dark/50 italic mt-0.5">{c.primary_property.name}</div>
                          )
                        )}
                      </>
                    ) : (
                      <span className="italic text-navy-dark/40">—</span>
                    )}
                  </td>
                  <td className="px-3 py-4 text-navy-dark text-center">{c.property_count}</td>
                  <td className="px-3 py-4 text-navy-dark text-center">{c.booking_count}</td>
                </tr>
              );
            })}
            {visibleCustomers.length === 0 && (
              <tr><td colSpan={6} className="px-6 py-16 text-center text-[13px] text-navy-dark/50">
                {activeConciergeId
                  ? "Keinem Kunden ist ein Objekt zugeteilt, das dieser Concierge betreut."
                  : t.team.no_customers}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
